import { RelatedUser, User } from './User'

export type BankAccount = {
  bankName?: string
  bankCode?: string
  branchName?: string
  branchCode?: string
  accountType: number
  accountNumber?: string
  accountOwner?: string
  payAfterHolidays: boolean
}

export type Address = {
  zipCode?: string
  address?: string
}

export type CompanyBasic = {
  name: string
  representativeName?: string
  representativeRole?: string
  uid?: string
  invoiceLicenseNumber?: string | null
  invoiceLessActionType?: number | null
  fiscalPeriod?: number
  phoneNumber?: string
  phoneNumberMobile?: string
  covenant?: string | null
  jobId?: string | null
  representative?: {
    userId: string
    submittedAt: string | null
    acceptedAt: string | null
    rejectedAt: string | null
    rejectReason: string | null
  } | null
} & Address

export type AggregateTransaction = {
  aggregateTransactions: boolean
  fixMonth: number | null
  fixDay: number | null
}

export type CompanyAttributes = CompanyBasic & BankAccount

export type CompanyMembershipFee = {
  name: string
  isTaxIn: boolean
  useRate: boolean
  chart: number[][]
  maximumFee: number | null
  useTax: boolean
  roundingType: number
} | null

export type TransactionPropConfig = {
  customProp1: {
    name: string
    required: boolean
  }
  customProp2: {
    name: string
    required: boolean
  }
  customProp3: {
    name: string
    required: boolean
  }
  customProp4: {
    name: string
    required: boolean
  }
}

export type DetailPropConfig = {
  requiredPic?: boolean
  requiredDeliveryDate?: boolean
  defaultProp: {
    name: string
    required: boolean
  }
  customProp1: {
    name: string
    required: boolean
  }
  customProp2: {
    name: string
    required: boolean
  }
}

export const CompanyTypeNormal = 'normal'
export const CompanyTypeRS = 'rs'

export type Company = {
  id: string
  uid: string
  type: 'normal' | 'rs'
  isRelatedMs: boolean
  isAccepted: boolean
  hasLagLessAccount?: boolean
  isInviter?: boolean
  membershipFee: CompanyMembershipFee
  detailPropConfig: DetailPropConfig
  transactionPropConfig: TransactionPropConfig
  taxRoundingType: number
  workAreaIds: string[]
  constructions: { typeId: string; detail?: string }[]
} & CompanyAttributes &
  AggregateTransaction

export type ClientDetail = {
  id: string
  uid: string
  name: string
  representativeName: string | null
  representativeRole: string | null
  address: string | null
  phoneNumber: string | null
  phoneNumberMobile: string | null
  invoice_license_number: string | null
  fiscal_period: number | null
  bankName: string | null
  bankCode: string | null
  branchName: string | null
  branchCode: string | null
  accountType: number | null
  accountCode: string | null
  accountOwner: string | null
  constructions: CompanyConstruction[]
  workAreaIds: string[]
  moneyspaceIds: string[]
  evaluation: CompanyEvaluation | null
  createdAt: string
}

export type CompanyConstruction = {
  typeId: string
  detail?: string
}

export type CompanyEvaluation = {
  totalPoint: number
  communicationSpeedPoint: number
  irregularitiesResponsePoint: number
  budgetPoint: number
  constructionSchedulePoint: number
  technologyPoint: number
  comments: CompanyEvaluationComment[]
}

type CompanyEvaluationComment = {
  id: string
  user: RelatedUser
  comment: string
  createdAt: string
}

export type Bank = {
  code: string
  name: string
}

export type BankBranch = {
  code: string
  name: string
}

export type CsvImportSetting = {
  bundlingKeyField: 'name' | 'custom_prop_1' | 'custom_prop_2' | 'custom_prop_3' | 'custom_prop_4'
  externalId: string
  name: string
  isTaxIn: string
  publishedAt: string
  note: string
  paymentDate: string
  closingDate: string
  deliveryDateFrom: string
  deliveryDateTo: string
  picId: string
  picSubId: string
  siteAddress: string
  customProp1: string
  customProp2: string
  customProp3: string
  customProp4: string
  detailDefaultProp: string
  detailCustomProp1: string
  detailCustomProp2: string
  detailUnitPrice: string
  detailQuantity: string
  detailUnit: string
  detailTaxBucketId: string
  skippingKeyField: string | null
  skippingTargetValues: { value: string }[]
}

export type CsvImportSettingGetResponse = {
  bundlingKeyField: 'name' | 'custom_prop_1' | 'custom_prop_2' | 'custom_prop_3' | 'custom_prop_4'
  externalId: string
  name: string
  isTaxIn: string
  publishedAt: string
  note: string
  paymentDate: string
  closingDate: string
  deliveryDateFrom: string
  deliveryDateTo: string
  picId: string
  picSubId: string
  siteAddress: string
  customProp1: string
  customProp2: string
  customProp3: string
  customProp4: string
  detailDefaultProp: string
  detailCustomProp1: string
  detailCustomProp2: string
  detailUnitPrice: string
  detailQuantity: string
  detailUnit: string
  detailTaxBucketId: string
  skippingKeyField: string | null
  skippingTargetValues: string[] | null
}

export type CompanyRepresentative = {
  id: string
  user: User
  company: Company
  submittedAt: string
  acceptedAt: string
  rejectedAt: string
  rejectReason: string
  certificationFiles: {
    url: string
    type: 'pdf' | 'image'
  }[]
}

export type BranchOffice = {
  id: string
  name: string
  members: string[]
}

export type VisitorRegistrationCompanyFields = {
  companyName: string
  companyKana: string
  companyZipCode: string
  companyAddress: string
  companyPhoneNumber: string
  representativeName: string
  representativeKana: string
  representativeBloodType: string
  representativeBirthDate: string
}

export type VisitorRegistrationFormParams = {
  survey1?: number
  survey2?: number
  survey3?: number
  survey4?: number
  survey4File?: File
  survey4FileId?: string
  survey4FileName?: string
  survey5?: number
  survey6?: number
  survey6Name?: string
  survey7?: number
  survey7Name?: string
  survey8?: number
  survey9?: number
  survey9File?: File
  survey9FileId?: string
  survey9FileName?: string
  survey10?: number
  survey11?: number
  survey12?: number
  survey13?: number
  survey14?: number
  survey15?: number
  survey16?: number
  survey16File?: File
  survey16FileId?: string
  survey16FileName?: string
  survey17?: number
  survey18?: number
  workCategory1: boolean
  workCategory2: boolean
  workCategory3: boolean
  workCategory4: boolean
  workCategory5: boolean
  workCategory6: boolean
  workCategory7: boolean
  workCategory8: boolean
  workCategory9: boolean
  workCategory10: boolean
  workCategory11: boolean
  workCategory12: boolean
  workCategory13: boolean
  workCategoryOther: boolean
  workCategoryOtherText?: string
  qualificationA1: boolean
  qualificationA2: boolean
  qualificationA3: boolean
  qualificationA4: boolean
  qualificationA5: boolean
  qualificationA6: boolean
  qualificationA7: boolean
  qualificationA8: boolean
  qualificationAOther: boolean
  qualificationAOtherText?: string
  qualificationB1: boolean
  qualificationB2: boolean
  qualificationB3: boolean
  qualificationB4: boolean
  qualificationB5: boolean
  qualificationB6: boolean
  qualificationB7: boolean
  qualificationB8: boolean
  qualificationBOther: boolean
  qualificationBOtherText?: string
  qualificationC1: boolean
  qualificationC2: boolean
  qualificationC3: boolean
  qualificationCOther: boolean
  qualificationCOtherText?: string
  qualificationD1: boolean
  qualificationD2: boolean
  qualificationD3: boolean
  qualificationD4: boolean
  qualificationD5: boolean
  qualificationD6: boolean
  qualificationD7: boolean
  qualificationD8: boolean
  qualificationD9: boolean
  qualificationDOther: boolean
  qualificationDOtherText?: string
}

export type VisitorRegistrationAnswers = {
  survey1: number
  survey2: number
  survey3?: number
  survey4: number
  survey4FileId?: string
  survey4FileName?: string
  survey5: number
  survey6: number
  survey6Name?: string
  survey7: number
  survey7Name?: string
  survey8: number
  survey9: number
  survey9FileId?: string
  survey9FileName?: string
  survey10: number
  survey11: number
  survey12: number
  survey13: number
  survey14: number
  survey15: number
  survey16: number
  survey16FileId?: string
  survey16FileName?: string
  survey17: number
  survey18: number
  workCategory1: boolean
  workCategory2: boolean
  workCategory3: boolean
  workCategory4: boolean
  workCategory5: boolean
  workCategory6: boolean
  workCategory7: boolean
  workCategory8: boolean
  workCategory9: boolean
  workCategory10: boolean
  workCategory11: boolean
  workCategory12: boolean
  workCategory13: boolean
  workCategoryOther: boolean
  workCategoryOtherText?: string
  qualificationA1: boolean
  qualificationA2: boolean
  qualificationA3: boolean
  qualificationA4: boolean
  qualificationA5: boolean
  qualificationA6: boolean
  qualificationA7: boolean
  qualificationA8: boolean
  qualificationAOther: boolean
  qualificationAOtherText?: string
  qualificationB1: boolean
  qualificationB2: boolean
  qualificationB3: boolean
  qualificationB4: boolean
  qualificationB5: boolean
  qualificationB6: boolean
  qualificationB7: boolean
  qualificationB8: boolean
  qualificationBOther: boolean
  qualificationBOtherText?: string
  qualificationC1: boolean
  qualificationC2: boolean
  qualificationC3: boolean
  qualificationCOther: boolean
  qualificationCOtherText?: string
  qualificationD1: boolean
  qualificationD2: boolean
  qualificationD3: boolean
  qualificationD4: boolean
  qualificationD5: boolean
  qualificationD6: boolean
  qualificationD7: boolean
  qualificationD8: boolean
  qualificationD9: boolean
  qualificationDOther: boolean
  qualificationDOtherText?: string
}

// フォームで使用する型（VisitorRegistrationAnswersにファイルフィールドを追加）
export type VisitorRegistrationAnswersFormValues = VisitorRegistrationAnswers & {
  survey4File?: File
  survey9File?: File
  survey16File?: File
}

export type VisitorRegistrationForm = {
  answers: VisitorRegistrationAnswers | null
} & VisitorRegistrationCompanyFields

export type VisitorRegistrationFormWorkerFields = {
  name: string
  kana: string
  gender: 1 | 2 | 3
  birthDate: string
  zipCode: string
  address: string
  phoneNumber: string
  qualification?: string
  bloodType: 'A' | 'B' | 'AB' | 'O'
  healthInsuranceName: string
  healthInsuranceLastDigits: string
  pensionInsuranceName: string
  pensionInsuranceLastDigits: string
  employmentInsuranceLastDigits?: string
}

export type VisitorRegistrationFormWorker = {
  id: string
} & VisitorRegistrationFormWorkerFields

export type VisitorRegistrationFormSubcontractorFields = {
  name: string
  kana: string
  representativeName: string
  representativeKana: string
  zipCode: string
  address: string
  phoneNumber: string
}

export type VisitorRegistrationFormSubcontractor = {
  id: string
} & VisitorRegistrationFormSubcontractorFields

export type VisitorRegistrationRecordWorker = {
  id: string
  name: string
  kana: string
  gender: 1 | 2 | 3
  birthDate: string
  zipCode: string
  address: string
  phoneNumber: string
  qualification?: string
  bloodType: 'A' | 'B' | 'AB' | 'O'
  healthInsuranceName: string
  healthInsuranceLastDigits: string
  pensionInsuranceName: string
  pensionInsuranceLastDigits: string
  employmentInsuranceLastDigits?: string
}

export type VisitorRegistrationRecordSubcontractor = {
  id: string
  name: string
  kana: string
  representativeName: string
  representativeKana: string
  zipCode: string
  address: string
  phoneNumber: string
}

export type VisitorRegistrationRecord = {
  id: string
  company: {
    id: string
    name: string
  }
  client: {
    id: string
    name: string
  }
  answers: VisitorRegistrationAnswers
  workers: VisitorRegistrationRecordWorker[]
  subcontractors: VisitorRegistrationRecordSubcontractor[]
  createdAt: string
  updatedAt: string
} & VisitorRegistrationCompanyFields

export type VisitorRegistrationRecordSet = {
  submittedRecords: VisitorRegistrationRecord[]
  receivedRecords: VisitorRegistrationRecord[]
}

export type CompanyAgreementUser = {
  user: RelatedUser
  step: number
}

export type CompanyAgreementSettings = {
  users: CompanyAgreementUser[]
}
